import HttpLibrary from '../libraries/HttpLibrary';
import SecureHttpLibrary from "../libraries/SecureHttpLibrary";

/**
 * Service for making requests to the Gateway API
 */
export default {

    /**
     * Download generated file.
     *
     * @param file
     * @returns {AxiosPromise<any>}
     */
    downloadFile (file) {
        return new HttpLibrary().get(file);
    },

    /**
     * Attempts to authenticate
     *
     * @param email
     * @param password
     * @returns {AxiosPromise<any>|*}
     */
    authenticate (email, password) {
        return new HttpLibrary().post(`/auth/login`, { email, password });
    },

    /**
     * Request a password reset.
     *
     * @param email
     * @returns {AxiosPromise|*}
     */
    forgotPassword (email) {
        return new HttpLibrary().post(`/user/password/reset`, { email })
    },

    /**
     * Reset password from request.
     *
     * @param password
     * @param token
     * @returns {AxiosPromise|*}
     */
    resetPassword (password, token) {
        return new HttpLibrary().post(`/user/password/reset/${token}`, { password });
    },

    /**
     * Attempts to activate user
     *
     * @param password
     * @param token
     * @returns {AxiosPromise|*}
     */
    activate (password, token) {
        return new HttpLibrary().post(`/user/activate`, { password, token });
    },

    /**
     * Get the suppliers bookings for a given period by either arrival or booking date.
     *
     * @param bookingFrom
     * @param bookingTo
     * @param arrivalFrom
     * @param arrivalTo
     * @param supplierId
     * @param token
     * @returns {AxiosPromise<any>}
     */
    getBookings (bookingFrom, bookingTo, arrivalFrom, arrivalTo, supplierId, token) {
        return new SecureHttpLibrary().get(`/booking`, token, {
            'booking-from': bookingFrom,
            'booking-to': bookingTo,
            'arrival-from': arrivalFrom,
            'arrival-to': arrivalTo,
            'supplierId': supplierId,
        });
    },

    /**
     * Retrieves revenue data for a given period
     *
     * @param bookingFrom
     * @param bookingTo
     * @param arrivalFrom
     * @param arrivalTo
     * @param supplierId
     * @param token
     * @returns {AxiosPromise<any>}
     */
    getRevenue (bookingFrom, bookingTo, arrivalFrom, arrivalTo, supplierId, token) {
        return new SecureHttpLibrary().get(`/statistics/revenue`, token, {
            'booking-from': bookingFrom,
            'booking-to': bookingTo,
            'arrival-from': arrivalFrom,
            'arrival-to': arrivalTo,
            'supplier-id': supplierId
        });
    },

    /**
     * Retrieves revenue data for a given period
     *
     * @param from
     * @param to
     * @param dateType
     * @param token
     * @returns {AxiosPromise<any>}
     */
    getInventory (from, to, dateType, token) {
        return new SecureHttpLibrary().get(`/statistics/inventory`, token, {
            from,
            to,
            'date-type': dateType
        });
    },

    /**
     * Retrieves a list of accommodations for a supplier
     *
     * @param supplierId
     * @param token
     * @returns {AxiosPromise<any>}
     */
    getAccommodationList (supplierId, token) {
        return new SecureHttpLibrary().get(`/accommodation/accommodation-sites/${supplierId}`, token);
    },

    /**
     * Retrieve allocation filter data consisting of array of sites with array of accommodations.
     *
     * @param accommodationSiteId
     * @param token
     * @returns {AxiosPromise<any>}
     */
    getAllocationFilterData(accommodationSiteId, token) {
        return new SecureHttpLibrary().get(`accommodation/sites/${accommodationSiteId}`, token);
    },

    /**
     * Retrieves supplier sites.
     *
     * @param supplierId
     * @param token
     * @returns {AxiosPromise.<any>}
     */
    getAllocationSupplierSites(supplierId, token) {
        return new SecureHttpLibrary().get(`accommodation/get-supplier-sites/${supplierId}`, token);
    },

    /**
     * Retrieves the list of allocations for a list of accommodations belonging to a specific site
     *
     * @param accommodationSiteIds
     * @param from
     * @param to
     * @param token
     * @returns {AxiosPromise|*}
     */
    getAllocationList (accommodationSiteIds, from, to, token) {
        return new SecureHttpLibrary().post(`allocation`, {
            accommodationSiteIds,
            from,
            to
        }, token);
    },

    /**
     * Retrieves the list of allocations for a list of accommodations belonging to a specific site merged and excluding
     * last updated by.
     *
     * @param accommodationSiteIds
     * @param from
     * @param to
     * @param token
     * @returns {AxiosPromise|*}
     */
    getAllocationListMerged (accommodationSiteIds, from, to, token) {
        return new SecureHttpLibrary().post(`allocation/merged`, {
            accommodationSiteIds,
            from,
            to
        }, token);
    },

    /**
     * Retrieves a list of sites with their accommdoations for the allocation filters
     *
     * @param token
     * @returns {AxiosPromise<any>}
     */
    getAllocationFilters (token) {
        return new SecureHttpLibrary().get(`allocation/filter`, token)
    },

    /**
     * Add the allocations for the accommodations selected for a specific site.
     *
     * @param accommodationSiteId
     * @param allocation
     * @param token
     * @returns {AxiosPromise|*}
     */
    addAllocations(accommodationSiteId, allocation, token) {
        return new SecureHttpLibrary().post(`allocation/add/${accommodationSiteId}`, allocation, token);
    },

    /**
     * Update allocation.
     *
     * @param accommodationSiteId
     * @param allocation
     * @param token
     * @returns {AxiosPromise|*}
     */
    updateAllocation(accommodationSiteId, allocation, token) {
        return new SecureHttpLibrary().post(`allocation/edit/${accommodationSiteId}`, {allocation: allocation}, token);
    },

    /**
     * Request update allocation.
     *
     * @param accommodationSiteId
     * @param allocation
     * @param token
     * @returns {AxiosPromise|*}
     */
    requestUpdateAllocation(accommodationSiteId, allocation, token) {
        return new SecureHttpLibrary().post(`allocation/edit/request/${accommodationSiteId}`, {allocation: allocation}, token);
    },

    /**
     * Block allocation.
     *
     * @param accommodationSiteId
     * @param period
     * @param token
     * @returns {AxiosPromise|*}
     */
    blockAllocation(accommodationSiteId, period, token) {
        return new SecureHttpLibrary().post(`allocation/block/${accommodationSiteId}`, period, token);
    },

    /**
     * Request block allocation.
     *
     * @param accommodationSiteId
     * @param period
     * @param token
     * @returns {AxiosPromise|*}
     */
    requestBlockAllocation(accommodationSiteId, period, token) {
        return new SecureHttpLibrary().post(`allocation/block/request/${accommodationSiteId}`, period, token);
    },

    /**
     * Get allocation calendar.
     *
     * @param accommodationSiteId
     * @param from
     * @param to
     * @param token
     * @returns {AxiosPromise<any>}
     */
    getAllocationCalendar(accommodationSiteId, from, to, token) {
        return new SecureHttpLibrary().get(`allocation/calendar/${accommodationSiteId}/${from}/${to}`, token);
    },

    /**
     * Get sub users for a supplier.
     *
     * @param supplierId
     * @param token
     * @returns {AxiosPromise.<any>}
     */
    getUsers(supplierId, token) {
        return new SecureHttpLibrary().get(`admin/users/all/${supplierId}`, token);
    },

    /**
     * Get a User by id.
     *
     * @param supplierId
     * @param userId
     * @param token
     * @returns {AxiosPromise.<any>}
     */
    getUser(supplierId, userId, token) {
        return new SecureHttpLibrary().get(`admin/users/${userId}/${supplierId}`, token);
    },

    /**
     * Create a new User.
     *
     * @param supplierId
     * @param token
     * @param payload
     * @returns {AxiosPromise|*}
     */
    createUser(supplierId, payload, token) {
        return new SecureHttpLibrary().post(`admin/users/create/${supplierId}`, payload, token);
    },

    /**
     * Edit information about a User.
     *
     * @param supplierId
     * @param user
     * @param token
     * @returns {AxiosPromise.<any>}
     */
    editUser(supplierId, user, token) {
        return new SecureHttpLibrary().put(`admin/users/update/${user.id}/${supplierId}`, user, token);
    },

    /**
     * Delete a User.
     *
     * @param userId
     * @param token
     * @returns {AxiosPromise|*}
     */
    deleteUser(userId, token) {
        return new SecureHttpLibrary().del(`admin/users/delete/${userId}`, token);
    },

    /**
     * Block a User.
     *
     * @param userId
     * @param token
     * @returns {AxiosPromise.<any>}
     */
    blockUser(userId, token) {
        return new SecureHttpLibrary().get(`admin/users/block/${userId}`, token);
    },

    /**
     * unblock a User.
     *
     * @param userId
     * @param token
     * @returns {AxiosPromise.<any>}
     */
    unblockUser(userId, token) {
        return new SecureHttpLibrary().get(`admin/users/unblock/${userId}`, token);
    },

    /**
     * Get list of roles for the supplier.
     *
     * @param supplierId
     * @param token
     * @returns {AxiosPromise<any>}
     */
    getRoles(supplierId, token) {
        return new SecureHttpLibrary().get(`admin/roles/${supplierId}`, token);
    },

    /**
     * Get list of supported languages.
     *
     * @param token
     * @returns {AxiosPromise<any>}
     */
    getLanguages(token) {
        return new SecureHttpLibrary().get(`languages`, token);
    },

    /**
     * Get list of requested changes.
     *
     * @param token
     * @param accommodationSiteId
     * @param from
     * @param to
     * @returns {AxiosPromise<any>}
     */
    getRequestList(token, accommodationSiteId, from, to) {
        return new SecureHttpLibrary().get(`allocation/requested/${accommodationSiteId}/${from}/${to}`, token);
    },

    /**
     * Request accept.
     *
     * @param token
     * @param payload
     * @returns {AxiosPromise<any>}
     */
    requestAccept(token, payload) {
        return new SecureHttpLibrary().post(`allocation/request/approve/${payload}`, {}, token);
    },

    /**
     * Accept the request edit allocation with modifications.
     *
     * @param token
     * @param payload
     * @returns {*}
     */
    requestAcceptModfied(token, payload) {
        return new SecureHttpLibrary().post(`allocation/request/modify/${payload.id}`, {
            allocation: payload.allocationObject
        }, token)
    },

    /**
     * Request accept.
     *
     * @param token
     * @param payload
     * @returns {AxiosPromise<any>}
     */
    requestDeny(token, payload) {
        return new SecureHttpLibrary().post(`allocation/request/decline/${payload}`, {}, token);
    },

    /**
     * Cancel a request.
     *
     * @param token
     * @param payload
     * @returns {AxiosPromise<any>}
     */
    requestCancel(token, payload) {
        return new SecureHttpLibrary().get(`allocation/request/cancel/${payload}`, token)
    },

    /**
     * Get the notification levels on user id.
     *
     * @param token
     * @param userId
     * @param supplierId
     * @returns {AxiosPromise<any>}
     */
    getNotificationsLevel(token, userId, supplierId) {
        return new SecureHttpLibrary().get(`admin/notifications/${userId}/${supplierId}`, token)
    },

    /**
     * Update the notification level for a user.
     *
     * @param supplierId
     * @param token
     * @param payload
     * @returns {AxiosPromise|*}
     */
    updateNotificationsLevel(supplierId, token, payload) {
        if(payload.id !== undefined) {
            return new SecureHttpLibrary().post(`admin/notifications/edit/${payload.id}/${supplierId}`, payload.notificationObject, token);
        } else {
            return new SecureHttpLibrary().post(`admin/notifications/edit/${supplierId}`, payload.notificationObject, token);
        }
    },

    /**
     * Toggle the users notification status.
     *
     * @param token
     * @param userId
     * @param supplierId
     * @returns {AxiosPromise<any>}
     */
    toggleNotifications(token, userId, supplierId) {
        return new SecureHttpLibrary().get(`admin/notifications/toggle/${userId}/${supplierId}`, token);
    },

    /**
     * Dismiss the specific notification.
     *
     * @param token
     * @param notificationId
     * @returns {AxiosPromise<any>}
     */
    dismissNotification(token, notificationId) {
        return new SecureHttpLibrary().get(`dashboard/allotment/dismiss/${notificationId}`, token);
    },

    /**
     * Retrieve account activity for last 6 months.
     *
     * @param supplierId
     * @param token
     * @returns {AxiosPromise<any>}
     */
    getAccountActivity(supplierId, token) {
        return new SecureHttpLibrary().get(`account/activity/${supplierId}`, token);
    },

    /**
     * Retrieve account notifications settings.
     *
     * @param token
     * @returns {AxiosPromise<any>}
     */
    getAccountNotifications(token) {
        return new SecureHttpLibrary().get(`account/notifications`, token);
    },

    /**
     * Get user account.
     *
     * @param token
     * @returns {AxiosPromise<any>}
     */
    getAccount(token) {
        return new SecureHttpLibrary().get(`account/get`, token);
    },

    /**
     * Update user.
     *
     * @param user
     * @param token
     * @returns {AxiosPromise|*}
     */
    updateUser(user, token) {
        return new SecureHttpLibrary().post(`account/update/account`, user, token);
    },

    /**
     * Update account password.
     *
     * @param oldPassword
     * @param newPassword
     * @param token
     * @returns {AxiosPromise|*}
     */
    updatePassword(oldPassword, newPassword, token) {
        return new SecureHttpLibrary().post(`account/update/password`, {
            password: newPassword,
            oldPassword: oldPassword
        }, token)
    },

    /**
     * Get availability for a specific supplier.
     *
     * @param token
     * @param supplierId
     * @returns {AxiosPromise<any>}
     */
    getAvailabilityBySupplierId(token, supplierId) {
        return new SecureHttpLibrary().get(`dashboard/allocation/${supplierId}`, token);
    },

    /**
     * Get notifications for a specific supplier.
     *
     * @param token
     * @param supplierId
     * @returns {AxiosPromise<any>}
     */
    getAccommodationNotificationsBySupplierId(token, supplierId) {
        return new SecureHttpLibrary().get(`dashboard/allotment/${supplierId}`, token);
    },

    /**
     * Send introduction sales confirmation.
     *
     * @param confirmationToken
     * @param ipAddress
     * @param browser
     * @returns {AxiosPromise<any>}
     */
    sendConfirmation(confirmationToken, ipAddress, browser) {
        return new SecureHttpLibrary().post(`introduction`, {
            confirmationToken,
            ipAddress,
            browser
        });
    },

    /**
     * Get all years where contracts exists.
     *
     * @returns {AxiosPromise.<any>}
     */
    getContractYears(token) {
        return new SecureHttpLibrary().get(`contractmanagement/years`, token);
    },

    /**
     * Get contract overview.
     *
     * @param token
     * @param year
     * @returns {Promise<any>}
     */
    getContractManagementssOverview(token, year) {
        return new SecureHttpLibrary().get(`contractmanagement/all/${year}`, token);
    },

    /**
     * Get contract by contract id.
     *
     * @param token
     * @param contractId
     */
    getContractManagementByContractId(token, contractId) {
        return new SecureHttpLibrary().get(`contractmanagement/${contractId}`, token);
    },

    /**
     * Save the contract.
     *
     * @param token
     * @param form
     * @param contractId
     * @returns {AxiosPromise|*}
     */
    saveContractManagementByContractId(token, form, contractId) {
        return new SecureHttpLibrary().postCustomHeaders(`contractmanagement/update/${contractId}`, form, token, {
            ContentType: 'multipart/form-data'
        })
    },

    /**
     * Download file from s3.
     *
     * @param token
     * @param file
     * @returns {AxiosPromise<any>}
     */
    contractManagementFormFileDownload(token, file) {
        return new SecureHttpLibrary().download(`get-file/contract/${file}`, token);
    },

    /**
     * Remove file from s3.
     *
     * @param token
     * @param file
     * @param extension
     * @param contractId
     * @returns {AxiosPromise<any>}
     */
    removeFileFromS3(token, file, extension, contractId) {
        return new SecureHttpLibrary().get(`contractmanagement/delete/${contractId}/file/${file}/${extension}`, token);
    },

    /**
     * Send contract completion email.
     *
     * @param token
     * @param contractId
     * @returns {AxiosPromise<any>}
     */
    contractManagementSendEmailBackOffice(token, contractId) {
        return new SecureHttpLibrary().get(`contractmanagement/complete/${contractId}`, token);
    },

    /**
     * Send contract readu for contract management review.
     *
     * @param token
     * @param contractId
     * @returns {AxiosPromise<any>}
     */
    contractManagementSendEmailReview(token, contractId) {
        return new SecureHttpLibrary().get(`contractmanagement/readyForReview/${contractId}`, token);
    },

    /**
     * Send contract readu for contract management review.
     *
     * @param token
     * @param contractId
     * @returns {AxiosPromise<any>}
     */
    contractManagementSendEmailSupplier(token, contractId) {
        return new SecureHttpLibrary().get(`contractmanagement/readyForSupplier/${contractId}`, token);
    },

    /**
     * Get the supplier data for the contract view.
     *
     * @param token
     * @param supplierId
     * @returns {Promise<any>}
     */
    getContractManagementSupplierData(token, supplierId) {
        return new SecureHttpLibrary().get(`contractmanagement/accommodations/sites/${supplierId}`, token);
    },

    /**
     * Get contract data by supplier id.
     *
     * @param token
     * @param supplierId
     * @param contractId
     * @returns {AxiosPromise<any>}
     */
    getContractBySupplierId(token, supplierId, contractId) {
        return new SecureHttpLibrary().get(`contract/full/${supplierId}/${contractId}`, token);
    },

    /**
     * Get contract accepted parts.
     *
     * @param token
     * @param contractId
     * @returns {AxiosPromise<any>}
     */
    getContractAcceptedParts(token, contractId) {
        return new SecureHttpLibrary().get(`contract/accepted/${contractId}`, token);
    },

    /**
     * Accept contract part.
     *
     * @param token
     * @param contractId
     * @param payload
     * @returns {AxiosPromise|*}
     */
    acceptContractPart(token, contractId, payload) {
        return new SecureHttpLibrary().post(`contract/accepted/${contractId}`, payload, token)
    },

    /**
     * Get contract notes.
     *
     * @param token
     * @param contractId
     * @returns {AxiosPromise<any>}
     */
    getContractNotes(token, contractId) {
        return new SecureHttpLibrary().get(`contract/notes/all/${contractId}`, token);
    },

    /**
     * Create contract note.
     *
     * @param token
     * @param contractId
     * @param note
     * @returns {AxiosPromise|*}
     */
    createContractNote(token, contractId, note) {
        return new SecureHttpLibrary().post(`contract/notes/create/${contractId}`, note, token);
    },

    /**
     * Get contract history by supplier id.
     *
     * @param token
     * @param supplierId
     * @returns {AxiosPromise<any>}
     */
    getContractHistoryBySupplierId(token, supplierId) {
        return new SecureHttpLibrary().get(`contractmanagement/history/${supplierId}`, token);
    },

    /**
     * Save the contract form data.
     *
     * @param token
     * @param data
     * @param supplierId
     * @param contractId
     * @returns {AxiosPromise<any>}
     */
    saveContractForm(token, data, contractId) {
        return new SecureHttpLibrary().post(`contract/notes/create/${contractId}`, {
            notes: data
        }, token);
    },

    /**
     * Finish the contract.
     *
     * @param token
     * @param supplierId
     * @param contractId
     * @param ipAddress
     * @param browser
     * @returns {AxiosPromise|*}
     */
    acceptContract(token, supplierId, contractId, ipAddress, browser) {
        return new SecureHttpLibrary().post(`contract/finish/${supplierId}/${contractId}`, {
            ipAddress,
            browser
        }, token);
    },

    /**
     * Download the contract pdf.
     *
     * @param token
     * @param fileName
     * @returns {AxiosPromise|*}
     */
    downloadContract(token, fileName) {
        return new SecureHttpLibrary().download(`get-file/signed/${fileName}`, token);
    },

    /**
     * Get bramds.
     *
     * @param token
     * @returns {AxiosPromise<any>}
     */
    getBrands(token) {
        return new SecureHttpLibrary().get(`brands`, token);
    },

    /**
     * Get a suppliers existing sites.
     *
     * @param token
     * @param supplierId
     * @returns {AxiosPromise.<any>}
     */
    getContractManagementCopiedSites(token, supplierId) {
        return new SecureHttpLibrary().get(`contractmanagement/existing-sites/${supplierId}`, token);
    },

    /**
     * Get a supplier existing accommodations based on an array of site ids.
     *
     * @param token
     * @param supplierId
     * @param siteIds
     * @returns {AxiosPromise|*}
     */
    getContractManagementCopiedAccommodations(token, supplierId, siteIds) {
        return new SecureHttpLibrary().post(`contractmanagement/existing-accommodations/${supplierId}`, {
            siteIds
        }, token);
    },

    /**
     * Send notification email.
     *
     * @param token
     * @param supplierId
     * @param contractId
     */
    sendContractEmailNotification(token, supplierId, contractId) {
        return new SecureHttpLibrary().get(`contract/notes/alert/${supplierId}/${contractId}`, token)
    },

    /**
     * Get arrival rules for accommodation site id in period.
     *
     * @param token
     * @param accommodationSiteId
     * @param from
     * @param to
     * @returns {AxiosPromise<any>}
     */
    getArrivalRules(token, accommodationSiteId, from, to) {
        return new SecureHttpLibrary().get(`accommodation/arrival/${accommodationSiteId}/${from}/${to}`, token);
    },

    /**
     * Get suppliers and products.
     *
     * @param token
     * @returns {AxiosPromise<any>}
     */
    getSuppliersAndProducts(token) {
        return new SecureHttpLibrary().get(`suppliers-products`, token);
    },

    /**
     * Get supplier booking filter data.
     *
     * @param supplierId
     * @param token
     * @returns {AxiosPromise<any>}
     */
    getSupplierFilterData(supplierId, token) {
        return new SecureHttpLibrary().get(`filter/${supplierId}`, token);
    },

    /**
     * Preview supplier contract draft.
     *
     * @param supplierId
     * @param contractId
     * @param token
     * @returns {AxiosPromise<any>}
     */
    previewContractDraft(supplierId, contractId, token) {
        return new SecureHttpLibrary().download(`contract/preview/${supplierId}/${contractId}`, token);
    }
}



// WEBPACK FOOTER //
// ./src/services/GatewayService.js